/**
 * Match review — provisional Site-Diary captures ↔ posted Cost Inbox invoices.
 *
 * Admin-only. Reached from the Cost Ledger. Every proposal is graded
 * (Strong / Likely / Possible) and nothing supersedes a capture until the
 * admin approves it here. Approving marks the capture SUPERSEDED: it leaves
 * every total while its photo stays as evidence on the invoice. Provisional
 * captures never reach Xero — this queue only ever links them to a posted
 * invoice line that already exists in the ledger.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AuthThumb } from './diary/AuthThumb';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Loader2, ArrowLeft, RefreshCw, CheckCircle2, X, Link2, Receipt, FileText,
  Layers, AlertTriangle, Clock, ChevronDown, Sparkles,
} from 'lucide-react';

const fmtGBP = (p) => ((typeof p === 'number' ? p : 0) / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const GRADE = {
  strong:   { label: 'Strong match', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  likely:   { label: 'Likely match', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
  possible: { label: 'Possible',     cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
};

function GradeBadge({ grade }) {
  const g = GRADE[grade] || GRADE.possible;
  return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border font-medium ${g.cls}`} data-testid="grade-badge">{g.label}</span>;
}

function CaptureCard({ capture }) {
  return (
    <div className="flex gap-3 min-w-0">
      <div className="shrink-0">
        <AuthThumb captureId={capture.capture_id} enabled={!!capture.capture_has_thumb} className="w-16 h-16 rounded-md" size="sm" testKey={`cap-${capture.id}`} />
      </div>
      <div className="min-w-0">
        <div className="text-white font-medium truncate">{capture.supplier_name || <span className="text-gray-500">No supplier</span>}</div>
        <div className="text-[11px] text-gray-400 truncate">
          <span className="text-gray-300">{capture.project_ref || 'Overhead'}</span> · <span className="capitalize">{capture.category}</span>
          {capture.details_only && <span className="ml-1 text-gray-500">· details only</span>}
        </div>
        <div className="text-[11px] text-gray-500 truncate">Captured {fmtDate(capture.created_at)}{capture.reference ? ` · ${capture.reference}` : ''}</div>
        <div className="text-sm text-white mt-0.5 tabular-nums">{fmtGBP(capture.net_pence)} <span className="text-[10px] text-gray-500">ex VAT{capture.value_pence !== capture.net_pence ? ` · ${fmtGBP(capture.value_pence)} inc` : ''}</span></div>
      </div>
    </div>
  );
}

function Signals({ cand }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {cand.vat_inclusive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">likely VAT-inclusive</span>}
      {cand.value_review && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">check value</span>}
      {(cand.signals || []).map((s, i) => (
        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-300 border border-white/10">{s}</span>
      ))}
    </div>
  );
}

function CandidateRow({ cand, onApprove, onDismiss, busy, testid }) {
  return (
    <div className="rounded-lg bg-[#232B38] border border-white/10 p-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <GradeBadge grade={cand.grade} />
            <span className="text-white text-sm inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-[#F5A623]" /> {cand.reference || 'Invoice'}</span>
            <span className="text-[11px] text-gray-500">{fmtDate(cand.invoice_date || cand.created_at)}</span>
          </div>
          <div className="text-sm text-white mt-0.5 tabular-nums">{fmtGBP(cand.net_pence)} <span className="text-[10px] text-gray-500">net · {fmtGBP(cand.value_pence)} gross</span></div>
          <Signals cand={cand} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={onApprove} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#08130d] font-semibold h-8" data-testid={`${testid}-approve`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Supersede</>}
          </Button>
          <Button size="sm" variant="outline" onClick={onDismiss} disabled={busy} className="border-white/20 text-gray-300 bg-white/5 hover:bg-white/10 h-8" title="Don't propose this pair again" data-testid={`${testid}-dismiss`}>
            <X className="w-3.5 h-3.5 mr-1" /> Not a match
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MatchReview() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [confirm, setConfirm] = useState(null); // { capIds, invoiceRowId, grade, label }

  useEffect(() => { if (user && !isAdmin) navigate('/portal'); }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/cost-ledger/match-queue');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load the match queue');
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const doApprove = async (capIds, invoiceRowId, grade, key) => {
    setBusyKey(key);
    try {
      const r = await api.post('/cost-ledger/match/approve', { invoice_row_id: invoiceRowId, capture_row_ids: capIds, grade });
      const n = r.data?.superseded || 0;
      toast.success(n === 1 ? 'Capture superseded — it now leaves every total (photo kept on the invoice)' : `${n} captures superseded`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusyKey(null); }
  };

  // Strong / likely approve directly; possible asks for confirmation first.
  const approve = (capIds, invoiceRowId, grade, key, label) => {
    if (grade === 'possible') { setConfirm({ capIds, invoiceRowId, grade, key, label }); return; }
    doApprove(capIds, invoiceRowId, grade, key);
  };

  const dismiss = async (capId, invoiceRowId, key) => {
    setBusyKey(key);
    try {
      await api.post('/cost-ledger/match/dismiss', { capture_row_id: capId, invoice_row_id: invoiceRowId });
      toast.success("Won't propose this pair again");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Dismiss failed'); }
    finally { setBusyKey(null); }
  };

  const counts = data?.counts || {};
  const statements = data?.statements || [];
  const proposals = data?.proposals || [];
  const chase = data?.chase || [];
  const nothing = !loading && statements.length === 0 && proposals.length === 0 && chase.length === 0;

  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-4 md:p-6" data-testid="match-review-page">
      <div className="max-w-[1000px] mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => navigate('/cost-ledger')} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-1" data-testid="match-review-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Cost Ledger
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Link2 className="w-6 h-6 text-[#F5A623]" /> Match review</h1>
            <p className="text-sm text-gray-400" data-testid="match-review-summary">
              {loading ? 'Loading…' : `${counts.proposals || 0} to review · ${counts.strong || 0} strong · ${counts.statements || 0} statement${counts.statements === 1 ? '' : 's'} · ${counts.chase || 0} to chase`}
            </p>
          </div>
          <Button onClick={load} disabled={loading} variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="match-review-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#2A3240]/50 p-3 mb-6 text-xs text-gray-400 flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-[#F5A623] shrink-0 mt-0.5" />
          <span>Provisional captures are on-site estimates logged before the supplier's invoice arrives. Approving a match <b className="text-white">supersedes</b> the capture — it stops counting so the posted invoice is the only cost, while its photo stays as evidence. Provisional captures are never sent to Xero.</span>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Building the match queue…</div>
        ) : nothing ? (
          <div className="p-12 text-center" data-testid="match-review-empty">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <div className="text-lg font-semibold text-white">Nothing to review</div>
            <div className="text-sm text-gray-400 mt-1">Every provisional capture is either matched or still waiting on its invoice.</div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Statements — one invoice clears several captures */}
            {statements.length > 0 && (
              <section data-testid="match-review-statements">
                <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Layers className="w-4 h-4 text-[#F5A623]" /> Statement invoices ({statements.length})</h2>
                <div className="space-y-3">
                  {statements.map(st => {
                    const key = `st-${st.inbox_item_id}`;
                    const capIds = st.captures.map(c => c.capture.id);
                    return (
                      <div key={st.inbox_item_id} className="rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/[0.06] p-4" data-testid={`statement-card-${st.inbox_item_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                          <div className="min-w-0">
                            <div className="text-white font-semibold inline-flex items-center gap-2"><FileText className="w-4 h-4 text-[#F5A623]" /> {st.supplier_name || 'Invoice'} · {st.reference || 'no ref'}</div>
                            <div className="text-[11px] text-gray-400">{st.project_ref || 'Overhead'} · {fmtDate(st.invoice_date)} · invoice net {st.invoice_net_pounds != null ? fmtGBP(st.invoice_net_pence) : '—'}</div>
                            <div className="text-[11px] mt-1">
                              {st.capture_count} captures total {fmtGBP(st.sum_net_pence)} net
                              {st.sums_to_invoice
                                ? <span className="ml-1 text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> add up to the invoice</span>
                                : <span className="ml-1 text-amber-300">· don't add up to the invoice net</span>}
                            </div>
                          </div>
                          <Button size="sm" onClick={() => approve(capIds, st.invoice_row_id, 'statement', key, `${st.capture_count} captures`)} disabled={busyKey === key}
                                  className="bg-emerald-500 hover:bg-emerald-400 text-[#08130d] font-semibold shrink-0" data-testid={`statement-approve-${st.inbox_item_id}`}>
                            {busyKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Supersede all {st.capture_count}</>}
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {st.captures.map(({ capture, candidate }) => (
                            <div key={capture.id} className="rounded-lg bg-black/20 border border-white/10 p-2.5 flex items-start justify-between gap-2" data-testid={`statement-capture-${capture.id}`}>
                              <div className="min-w-0"><CaptureCard capture={capture} /><div className="mt-1"><GradeBadge grade={candidate.grade} /></div></div>
                              <button onClick={() => dismiss(capture.id, st.invoice_row_id, `${key}-${capture.id}`)} disabled={busyKey === `${key}-${capture.id}`} className="shrink-0 text-gray-400 hover:text-white p-1" title="Don't propose this pair again" data-testid={`statement-dismiss-${capture.id}`}><X className="w-4 h-4" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Proposals — capture-centric */}
            {proposals.length > 0 && (
              <section data-testid="match-review-proposals">
                <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#F5A623]" /> Captures to match ({proposals.length})</h2>
                <div className="space-y-3">
                  {proposals.map(p => {
                    const cap = p.capture;
                    const top = p.candidates[0];
                    const rest = p.candidates.slice(1);
                    const keyTop = `p-${cap.id}-${top.invoice_row_id}`;
                    const isOpen = !!expanded[cap.id];
                    return (
                      <div key={cap.id} className="rounded-xl border border-white/10 bg-[#2A3240] p-4" data-testid={`proposal-card-${cap.id}`}>
                        {p.chase && (
                          <div className="mb-2 text-[11px] inline-flex items-center gap-1 text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5">
                            <AlertTriangle className="w-3 h-3" /> {p.age_days} days old · chase the supplier for the invoice
                          </div>
                        )}
                        <div className="mb-3"><CaptureCard capture={cap} /></div>
                        <CandidateRow
                          cand={top}
                          busy={busyKey === keyTop}
                          onApprove={() => approve([cap.id], top.invoice_row_id, top.grade, keyTop, top.reference || 'invoice')}
                          onDismiss={() => dismiss(cap.id, top.invoice_row_id, keyTop)}
                          testid={`proposal-top-${cap.id}`}
                        />
                        {rest.length > 0 && (
                          <div className="mt-2">
                            <button onClick={() => setExpanded(e => ({ ...e, [cap.id]: !e[cap.id] }))} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid={`proposal-more-${cap.id}`}>
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} /> {rest.length} other possible match{rest.length === 1 ? '' : 'es'}
                            </button>
                            {isOpen && (
                              <div className="space-y-2 mt-2">
                                {rest.map(c => {
                                  const k = `p-${cap.id}-${c.invoice_row_id}`;
                                  return (
                                    <CandidateRow key={c.invoice_row_id} cand={c} busy={busyKey === k}
                                      onApprove={() => approve([cap.id], c.invoice_row_id, c.grade, k, c.reference || 'invoice')}
                                      onDismiss={() => dismiss(cap.id, c.invoice_row_id, k)}
                                      testid={`proposal-alt-${cap.id}-${c.invoice_row_id}`} />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Chase — old captures with no confident match */}
            {chase.length > 0 && (
              <section data-testid="match-review-chase">
                <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-red-400" /> Chase supplier — over 60 days, no invoice yet ({chase.length})</h2>
                <div className="rounded-xl border border-red-500/25 bg-red-500/[0.06] divide-y divide-white/5">
                  {chase.map(({ capture, age_days }) => (
                    <div key={capture.id} className="p-3 flex items-center justify-between gap-3" data-testid={`chase-row-${capture.id}`}>
                      <CaptureCard capture={capture} />
                      <div className="text-right shrink-0">
                        <div className="text-red-300 text-sm font-medium">{age_days} days</div>
                        <div className="text-[11px] text-gray-500">no invoice found</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="match-approve-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supersede on a possible match?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The value doesn't match exactly, so please double-check this is the same spend. Once you confirm, {confirm?.label ? <b className="text-white">{confirm.label}</b> : 'the capture'} will stop counting and the invoice becomes the only cost. The photo stays on the invoice as evidence.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="match-approve-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const c = confirm; setConfirm(null); if (c) doApprove(c.capIds, c.invoiceRowId, c.grade, c.key); }}
              className="bg-emerald-500 hover:bg-emerald-400 text-[#08130d] font-semibold" data-testid="match-approve-confirm-ok">
              Supersede anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
